import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowRight, ArrowLeft, X, Share2, Check, Copy, Download } from "lucide-react";
import { loadFindings } from "../lib/loadFindings";
import { generateShareCard } from "../lib/shareCard";

function TagColor(tag = "") {
  const t = tag.toLowerCase();
  if (t.includes("flagship")) return "#FF4D00";
  if (t.includes("protocol")) return "#00F0FF";
  if (t.includes("method")) return "#FF4D00";
  return "#A3A3A3";
}

function CopyBibtex({ text }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div
      className="mt-10 border border-white/10 bg-[#0d0d0d]"
      data-testid="bibtex-block"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 bg-[#0a0a0a]">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#00F0FF]">
          ◆ cite · bibtex
        </div>
        <button
          onClick={copy}
          data-testid="copy-bibtex"
          className="font-mono text-[10px] tracking-[0.22em] uppercase flex items-center gap-1.5 text-neutral-400 hover:text-[#FF4D00] transition-colors"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5" /> copied
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" /> copy
            </>
          )}
        </button>
      </div>
      <pre className="px-5 py-4 overflow-x-auto font-mono text-[12px] text-neutral-300 whitespace-pre leading-relaxed">
        {text}
      </pre>
    </div>
  );
}

function ShareButton({ finding }) {
  const [generating, setGenerating] = useState(false);
  const onClick = async () => {
    try {
      setGenerating(true);
      await generateShareCard(finding);
    } finally {
      setTimeout(() => setGenerating(false), 900);
    }
  };
  return (
    <button
      onClick={onClick}
      data-testid="share-finding-btn"
      className="font-mono text-[11px] tracking-[0.22em] uppercase flex items-center gap-2 border border-[#FF4D00]/60 text-[#FF4D00] px-4 py-2.5 hover:bg-[#FF4D00]/10 hover:shadow-[0_0_18px_rgba(255,77,0,0.3)] transition-all disabled:opacity-60"
      disabled={generating}
    >
      {generating ? (
        <>
          <Download className="w-3.5 h-3.5" /> rendering…
        </>
      ) : (
        <>
          <Share2 className="w-3.5 h-3.5" /> share as png
        </>
      )}
    </button>
  );
}

export default function BlogFeed() {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(null);
  const [err, setErr] = useState(null);
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 5;

  useEffect(() => {
    loadFindings()
      .then(setPosts)
      .catch((e) => setErr(e.message));
  }, []);

  return (
    <section
      id="updates"
      data-testid="updates-section"
      className="relative pt-32 pb-16 px-6 lg:px-10 border-t border-white/5"
    >
      <div className="absolute inset-0 tech-grid opacity-25 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="flex items-end justify-between flex-wrap gap-6 mb-12"
        >
          <div>
            <div className="chapter-label mb-6">06 · Research Updates</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-neutral-100 leading-tight max-w-3xl">
              A lab notebook, written at 3 AM.
            </h2>
          </div>
          <div className="flex flex-col items-start lg:items-end gap-2 font-mono text-[11px] text-neutral-600 tracking-widest">
            <span>{posts.length} · ENTRIES</span>
            <span>DROP-IN MARKDOWN · public/content/findings/</span>
            <a
              href="/rss.xml"
              target="_blank"
              rel="noreferrer"
              data-testid="rss-link"
              className="text-[#FF4D00] hover:text-[#FF7A3A] transition-colors"
            >
              ↗ rss feed
            </a>
          </div>
        </motion.div>

        {err && (
          <div className="border border-red-900/60 bg-red-950/20 text-red-300 p-5 font-mono text-xs">
            Could not load findings: {err}
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-5">
          {posts.length === 0 && !err && [0,1,2,3].map((i) => (
            <div key={i} className="border border-white/10 bg-[#0d0d0d] p-7 animate-pulse w-full md:w-[calc(50%-0.625rem)]">
              <div className="flex items-center justify-between mb-4">
                <div className="h-3 w-24 bg-white/5 rounded" />
                <div className="h-3 w-16 bg-white/5 rounded" />
              </div>
              <div className="h-7 w-3/4 bg-white/5 rounded mb-4" />
              <div className="space-y-2">
                <div className="h-3 w-full bg-white/5 rounded" />
                <div className="h-3 w-5/6 bg-white/5 rounded" />
              </div>
              <div className="mt-6 h-3 w-32 bg-white/5 rounded" />
            </div>
          ))}
          {posts.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map((f, i) => (
            <motion.article
              key={f.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`finding-${f.id}`}
              className="group border border-white/10 bg-[#0d0d0d] p-7 hover:border-[#FF4D00]/40 transition-colors cursor-pointer relative overflow-hidden w-full md:w-[calc(50%-0.625rem)]"
              onClick={() => setOpen(f)}
            >
              <div className="absolute top-0 left-0 h-px w-0 group-hover:w-full transition-all duration-[900ms] bg-[#FF4D00]" />
              <div className="flex items-center justify-between mb-4">
                <span
                  className="font-mono text-[10px] tracking-[0.28em] uppercase"
                  style={{ color: TagColor(f.tag) }}
                >
                  {f.tag}
                </span>
                <time className="font-mono text-[10px] text-neutral-500 tracking-widest">
                  {f.date}
                </time>
              </div>
              <h3 className="font-serif-display text-2xl lg:text-3xl text-neutral-100 leading-tight group-hover:text-[#FF4D00] transition-colors">
                {f.title}
              </h3>
              <p className="mt-4 text-sm text-neutral-400 leading-relaxed">
                {f.summary}
              </p>
              <div className="mt-6 flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-[#FF4D00]">
                read full analysis
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.article>
          ))}
        </div>

        {posts.length > ITEMS_PER_PAGE && (
          <div className="mt-16 flex justify-center items-center gap-3">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-10 h-10 border border-white/10 flex items-center justify-center text-neutral-400 hover:text-[#FF4D00] hover:border-[#FF4D00]/60 transition-colors disabled:opacity-30 disabled:pointer-events-none"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.ceil(posts.length / ITEMS_PER_PAGE) }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i + 1)}
                className={`w-10 h-10 border flex items-center justify-center font-mono text-xs transition-colors ${
                  page === i + 1
                    ? "border-[#FF4D00]/60 text-[#FF4D00] bg-[#FF4D00]/10"
                    : "border-white/10 text-neutral-400 hover:border-[#FF4D00]/40 hover:text-neutral-200"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(Math.ceil(posts.length / ITEMS_PER_PAGE), p + 1))}
              disabled={page === Math.ceil(posts.length / ITEMS_PER_PAGE)}
              className="w-10 h-10 border border-white/10 flex items-center justify-center text-neutral-400 hover:text-[#FF4D00] hover:border-[#FF4D00]/60 transition-colors disabled:opacity-30 disabled:pointer-events-none"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Reader modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(null)}
            data-testid="finding-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.35 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-3xl mx-auto my-16 border border-white/15 bg-[#0a0a0a] p-8 lg:p-12 relative"
            >
              <button
                data-testid="finding-modal-close"
                onClick={() => setOpen(null)}
                className="absolute top-5 right-5 w-10 h-10 border border-white/10 hover:border-[#FF4D00]/60 flex items-center justify-center text-neutral-400 hover:text-[#FF4D00] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-4 mb-6">
                <span
                  className="font-mono text-[10px] tracking-[0.28em] uppercase"
                  style={{ color: TagColor(open.tag) }}
                >
                  {open.tag}
                </span>
                <span className="font-mono text-[10px] text-neutral-500 tracking-widest">
                  {open.date}
                </span>
              </div>
              <h3 className="font-serif-display text-3xl lg:text-5xl text-neutral-100 leading-tight">
                {open.title}
              </h3>

              <div className="mt-8 prose-invert font-mono text-[15px] text-neutral-300 leading-relaxed space-y-5">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: (props) => <p {...props} />,
                    strong: ({ node, ...p }) => (
                      <strong className="text-neutral-100" {...p} />
                    ),
                    em: ({ node, ...p }) => (
                      <em className="text-[#FF4D00] not-italic" {...p} />
                    ),
                    a: ({ node, ...p }) => (
                      <a
                        {...p}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#00F0FF] underline decoration-[#00F0FF]/40 underline-offset-4 hover:decoration-[#00F0FF]"
                      />
                    ),
                    code: ({ node, ...p }) => (
                      <code
                        className="px-1.5 py-0.5 bg-[#151515] border border-white/10 text-[#FF4D00] text-[13px]"
                        {...p}
                      />
                    ),
                    h2: ({ node, ...p }) => (
                      <h2
                        className="font-serif-display text-2xl text-neutral-100 mt-8 mb-2"
                        {...p}
                      />
                    ),
                    ul: ({ node, ...p }) => (
                      <ul className="list-none space-y-2 pl-0" {...p} />
                    ),
                    li: ({ node, ...p }) => (
                      <li className="flex gap-3 before:content-['▸'] before:text-[#FF4D00]" {...p} />
                    ),
                  }}
                >
                  {open.body || ""}
                </ReactMarkdown>
              </div>

              <CopyBibtex text={open.bibtex} />

              <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-4">
                <div className="font-mono text-[10px] text-neutral-600 tracking-widest">
                  PROMETHEUS·EBM · #{open.id}
                </div>
                <ShareButton finding={open} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
